import { Injectable } from '@angular/core';
import { FormArray, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import {
  AssessmentAnswerInterface,
  AssessmentPageInterface,
  AssessmentQuestionGroupInterface,
  AssessmentQuestionInterface,
} from 'app/assessment/models';

@Injectable()
export class CreateEntityForm {
  private answersNotRequired: boolean = false;
  private readOnly: boolean = false;

  public createFormArray(pages: AssessmentPageInterface[], optionalAnswers?: boolean, readOnly?: boolean): FormArray {
    const array: FormGroup[] = [];

    this.answersNotRequired = !!optionalAnswers;
    this.readOnly = readOnly;

    // PAGES LOOP
    pages.forEach((page: AssessmentPageInterface) => {
      array.push(this.createPageFormGroup(page));
    });

    return new FormArray(array);
  }

  private createPageFormGroup(page: AssessmentPageInterface): FormGroup {
    const group: any = {};

    // QUESTION GROUPS LOOP
    const questionGroups: AssessmentQuestionGroupInterface[] = page.questionGroups;
    questionGroups.forEach((questionGroup: AssessmentQuestionGroupInterface) => {
      group[questionGroup.id] = this.createQuestionGroupFormGroup(questionGroup);
    });

    return new FormGroup(group);
  }

  private createQuestionGroupFormGroup(questionGroup: AssessmentQuestionGroupInterface): FormGroup {
    const group: any = {};

    // QUESTIONS LOOP
    const questions: AssessmentQuestionInterface[] = questionGroup.questions;
    for (const questionKey in questions) {
      if (!questions.hasOwnProperty(questionKey)) { continue; }

      const question: AssessmentQuestionInterface = questions[questionKey];
      if (question.isMultipleChoice) {
        group[question.id] = this.createMultiChoiceGroup(question);
      } else {
        group[question.id] = this.populateAnswers(question);
      }

      // some questions don't have child questions
      if (question.childQuestions.length > 0) {
        // FOLLOW-UP QUESTION GROUPS LOOP
        const followUpQuestions: AssessmentQuestionInterface[] = question.childQuestions;
        followUpQuestions.forEach((followUpQuestion: AssessmentQuestionInterface) => {
          if (followUpQuestion.isMultipleChoice) {
            group[`${question.id}-${followUpQuestion.id}`] = this.createMultiChoiceGroup(followUpQuestion, true);
          } else {
            group[`${question.id}-${followUpQuestion.id}`] = this.populateAnswers(followUpQuestion, true);
          }
        });
      }
    }

    return new FormGroup(group);
  }

  private populateAnswers(question: AssessmentQuestionInterface, isFollowUp?: boolean): FormControl {
    let formControl: FormControl;
    const answers: AssessmentAnswerInterface[] = question.answers;

    // sometimes questions don't have answers (they'll have childQuestions though)
    if (answers.length <= 0) {
      return new FormControl({ value: '', disabled: this.readOnly });
    }

    for (const answerKey in answers) {
      if (!answers.hasOwnProperty(answerKey)) { continue; }

      // populating the form if answers exist
      if (answers[answerKey].isSelected) {
        // discomfort body chart & photo upload are optional
        if (question.group === 'DC' || question.group === 'EPIC') {
          formControl = new FormControl({ value: answers[answerKey], disabled: this.readOnly });
          break;
        }

        if (this.answersNotRequired) {
          formControl = new FormControl({ value: answers[answerKey], disabled: this.readOnly });
        } else {
          formControl = new FormControl({ value: answers[answerKey], disabled: this.readOnly }, Validators.required);
        }
        break;
      }

      // if the question isn't a follow-up, discomfort body chart, or photo upload, don't validate
      if (!isFollowUp && question.group !== 'DC' && question.group !== 'EPIC') {
        if (this.answersNotRequired) {
          formControl = new FormControl({ value: '', disabled: this.readOnly });
        } else {
          formControl = new FormControl({ value: '', disabled: this.readOnly }, Validators.required);
        }
        continue;
      }

      formControl = new FormControl({ value: '', disabled: this.readOnly });
    }

    return formControl;
  }

  private createMultiChoiceGroup(question: AssessmentQuestionInterface, isFollowUp?: boolean): FormGroup {
    const group: any = {};
    const validatorFn: ValidatorFn = this.getValidatorFn(question);

    // ANSWERS LOOP
    const answers: AssessmentAnswerInterface[] = question.answers;
    for (const answerKey in answers) {
      if (!answers.hasOwnProperty(answerKey)) { continue; }

      const answer: AssessmentAnswerInterface = answers[answerKey];
      if (isFollowUp) {
        group[answer.id] = new FormControl({ value: answer.isSelected, disabled: this.readOnly });
        continue;
      }

      group[answer.id] = new FormControl({ value: answer.isSelected, disabled: this.readOnly }, validatorFn);
    }

    const formGroup: FormGroup = new FormGroup(group, (formGroup: FormGroup): {} => {
      // if question isn't a follow-up, don't validate (for now)
      // will need a custom validator to make this required when parent answer is selected
      if (!isFollowUp && validatorFn) {
        return this.validateMultiChoice(formGroup);
      }
      return null;
    });

    return formGroup;
  }

  /**
   * Determine what if any validator function should be applied to the provided question.
   *
   * @param question Assessment question
   */
  private getValidatorFn(question: AssessmentQuestionInterface): ValidatorFn {
    if (this.answersNotRequired) {
      return undefined;
    }

    // If it is "Reported Accessories", don't make it required.
    if (question.group === 'ECAccessories' && question.id === 4) {
      return undefined;
    }

    return Validators.required;
  }

  // at least one checkbox has to be checked for that question to be validated
  private validateMultiChoice(formGroup: FormGroup): ValidationErrors {
    for (const key in formGroup.controls) {
      if (!formGroup.controls.hasOwnProperty(key)) { continue; }

      const control: FormControl = <FormControl>formGroup.controls[key];
      if (control.value) {
        return null;
      }
    }

    return {
      validateMultiChoice: false,
    };
  }
}
